import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  Factory,
  ShoppingCart,
  Users,
  Receipt,
  Truck,
  MapPin,
  QrCode,
  Warehouse,
  Globe,
  UserCog,
  BarChart3,
  Sparkles,
  PackageSearch,
  MessageSquare,
  Palette,
  LineChart,
  ScrollText,
  Bot,
  CreditCard,
  FileText,
  Store,
  Building2,
  FileCheck2,
  KeyRound,
  EyeOff,
  Check,
  ChevronDown,
  MessagesSquare,
  CalendarClock,
  Scale,
  Mail,
  Smartphone,
} from 'lucide-react';
import PublicHeader from '../../components/Public/PublicHeader';
import PublicFooter from '../../components/Public/PublicFooter';
import LandingSideToc from '../../components/Public/LandingSideToc';
import LandingEcosystem from '../../components/Public/LandingEcosystem';
import LandingOptionalImage from '../../components/Public/LandingOptionalImage';
import LogoImage from '../../components/Common/LogoImage';
import { getAppDomainUrl } from '../../utils/constants';
import { W7C_ECOSYSTEM_PRESENTATIONS_URL } from '../../utils/externalLinks';
import { LANDING_PAGE_CSS_VARS, lpFile } from '../../utils/landingPageAssets';
import { PLATFORM_BRANDING_SITE_LOGO, publicHeaderLogoSources } from '../../utils/platformAssets';
import { openLandingAgent } from '../../components/Public/AgentLauncher/useLandingAgentChat';
import useSEO from '../../hooks/useSEO';
import './LandingPage.css';

/* ============================================================
   MARKBASE LANDING PAGE
   Ассеты: /api/platform-assets/landing/ (SVG в git; JPG — volume при деплое)
   ============================================================ */

const ICON_BASE = '/api/platform-assets/moduls/icons';

const APP_REGISTER_URL = () => getAppDomainUrl('/register');

/* Маппинг module → URL для карточек кирпичей:
   - публичные страницы markbase.ru (product/*) → остаются как есть
   - страницы внутри приложения → ведут на app.markbase.ru/login  */
const MODULE_ROUTES = {
  shop:          '/product/core',
  market:        '/product/core',
  orders:        '__app__/login',
  crm:           '/product/crm-hrm',
  delivery:      '/product/logistics',
  seo:           '/pricing',
  bonus:         '/pricing',
  loyalty:       '/pricing',
  wms:           '/product/logistics',
  logistics:     '/product/logistics',
  constructor:   '/product/constructor',
  import:        '__app__/login',
  services:      '/product/services',
  registries:    '__app__/login',
  documents:     '/product/logistics',
  reports:       '/product/analytics',
  hrm:           '/product/crm-hrm',
  chats:         '/product/crm-hrm',
  analytics:     '/product/analytics',
  integrations:  '__app__/login',
  billing:       '/pricing',
  tariffs:       '/pricing',
  files:         '/product/core',
  notifications: '/product/core',
  security:      '/product/security',
  settings:      '__app__/login',
  auth:          '/product/security',
  roles:         '/product/security',
  processes:     '/product/core',
  monitoring:    '/product/core',
  scheduler:     '/product/core',
};

/** Разрешает маршрут из MODULE_ROUTES в итоговый URL */
function resolveModuleUrl(route) {
  if (!route) return getAppDomainUrl('/login');
  if (route.startsWith('__app__')) return getAppDomainUrl(route.replace('__app__', ''));
  return route; // относительный путь markbase.ru — обрабатывается react-router
}

/* Лента возможностей под hero — одна строка смысла «всё на одних данных» */
const HERO_CHAT_HINTS = [
  'Соберу витрину из вашего прайса',
  'Заведу товары, услуги и цифровые',
  'Подключу Telegram, MAX и VK',
  'Открою ЛК покупателя и кабинет работы',
];

const HERO_CAPABILITY_BADGES = [
  'Товары',
  'Услуги и запись',
  'Цифровые товары',
  'Своя витрина',
  'Свой домен',
  'Telegram',
  'MAX',
  'VK',
  'ЛК покупателя',
  'Кабинет работы',
  'Заказы',
  'Склад и доставка',
  'Документы и оплата',
  'Клиенты (CRM)',
];

/* «Кому подходит»: четыре типа бизнеса, каждый ведёт в чат со своим вопросом */
const WHO_CARDS = [
  {
    id: 'retail',
    Icon: Store,
    image: 'b14_who_shop.jpg',
    title: 'Товары',
    text: 'Каталог, цена и остаток заводятся один раз — витрина, опт и каналы берут те же карточки.',
    prompt: 'Продаю товары: нужен каталог, своя витрина и каналы продаж из одних остатков. С чего начать в Markbase?',
  },
  {
    id: 'services',
    Icon: CalendarClock,
    image: 'b15_who_services.jpg',
    title: 'Услуги и запись',
    text: 'Расписание, специалисты и оплата: клиент записывается сам, вы видите загрузку дня.',
    prompt: 'У меня услуги с записью. Нужны расписание, специалисты, оплата и кабинет клиента. Как запустить это в Markbase?',
  },
  {
    id: 'digital',
    Icon: KeyRound,
    image: 'b16_who_digital.jpg',
    title: 'Цифровые товары',
    text: 'Ключи, файлы и доступы выдаются после оплаты автоматически — склад для этого не нужен.',
    prompt: 'Продаю цифровые товары: ключи, файлы и доступы. Как настроить выдачу после оплаты в Markbase?',
  },
  {
    id: 'channels',
    Icon: Smartphone,
    image: 'b17_who_warehouse.jpg',
    title: 'Кабинеты и каналы',
    text: 'ЛК покупателя, рабочее место сотрудников и витрины в сайт, Telegram, MAX и VK — из одного кабинета.',
    prompt: 'Нужны своя витрина, ЛК покупателя, кабинет работы для сотрудников и каналы Telegram, MAX, VK. Как это устроено в Markbase?',
  },
];

/* Сценарии «AI за вас»: заголовок, иллюстрация и предзаполненный вопрос для чата */
const AI_FLOW_CARDS = [
  {
    id: 'catalog',
    Icon: Package,
    title: 'Каталог из вашего файла',
    image: 'b09_catalog_upload.jpg',
    steps: [
      'Прайс, Excel или список в любом виде',
      'AI собирает карточки товаров, услуг и цифрового',
      'Вы правите готовое — не набираете каталог руками',
    ],
    cta: 'Собрать каталог в чате',
    prompt:
      'Помоги собрать каталог: есть файл с товарами, услугами и цифровыми позициями, колонки названы по-своему. С чего начать в Markbase?',
  },
  {
    id: 'tariff',
    Icon: Scale,
    title: 'Тариф под ваш объём',
    image: 'b10_tariff_scales.jpg',
    steps: [
      'Скажите объём каталога, витрин и сотрудников',
      'AI считает, какие модули нужны сейчас',
      'Остальное подключается позже, без переплаты',
    ],
    cta: 'Подобрать тариф в чате',
    prompt:
      'У нас каталог товаров и услуг, одна витрина и 3 сотрудника. Какой тариф Markbase подойдёт и что можно подключить позже?',
  },
  {
    id: 'channels',
    Icon: Smartphone,
    title: 'Витрины в сайт, Telegram, MAX и VK',
    image: 'b11_marketplace_keys.jpg',
    steps: [
      'Один каталог — несколько витрин и каналов',
      'Свой домен, мессенджеры и маркетплейсы',
      'Заказы возвращаются в один кабинет',
    ],
    cta: 'Разобрать каналы в чате',
    prompt:
      'Хочу свою витрину на домене и отдельные витрины в Telegram, MAX и VK из одного каталога. Как это настроить в Markbase?',
  },
  {
    id: 'mail',
    Icon: Mail,
    title: 'Почта и заявки без разбора руками',
    image: 'b12_mail_assistant.jpg',
    steps: [
      'Подключаете рабочий ящик',
      'AI выделяет заявки, цены и сроки',
      'Черновик заказа — вам остаётся согласиться',
    ],
    cta: 'Показать разбор почты',
    prompt: 'Нам пишут заявки в свободной форме. Как Markbase превратит письма в заказы на товары и услуги?',
  },
];

const CASE_POINTS = [
  {
    id: 'site',
    Icon: Store,
    title: 'Свой сайт и домен',
    text: 'Товары, услуги и цифровое на вашей витрине, не на чужой площадке.',
    prompt: 'Хочу свою витрину на своём домене. Как запустить это в Markbase?',
  },
  {
    id: 'cabinets',
    Icon: Users,
    title: 'ЛК и кабинет работы',
    text: 'Покупатель заходит в ЛК, сотрудники — в рабочее место.',
    prompt: 'Нужны ЛК покупателя и кабинет работы для сотрудников. Как это устроено в Markbase?',
  },
  {
    id: 'messengers',
    Icon: Smartphone,
    title: 'Telegram, MAX и VK',
    text: 'Несколько витрин из одного каталога, без второй сверки остатков.',
    prompt: 'Хочу витрины в Telegram, MAX и VK из одного каталога. Как настроить каналы в Markbase?',
  },
  {
    id: 'docs',
    Icon: FileText,
    title: 'Заказы и документы',
    text: 'Сметы, счета и отгрузка собираются из того же заказа.',
    prompt: 'Как в Markbase заказы, сметы и документы живут в одном кабинете?',
  },
];

/* Единый источник данных: путь одного факта от файла до документа */
const DATA_FLOW_STEPS = [
  { title: 'Импорт', text: 'Приносите прайсы и выгрузки — в любом виде' },
  { title: 'Сведение', text: 'Артикул и штрихкод сами находят дубли' },
  { title: 'Каталог', text: 'Одна карточка товара на всю компанию' },
  { title: 'Каналы', text: 'Витрина, маркетплейсы и опт берут одни данные' },
  { title: 'Заказ', text: 'Склад, доставка и оплата видят один состав' },
  { title: 'Документы', text: 'Счёт и акт собираются из этого же заказа' },
];

/* Полный контур операций: одна цепочка от товара до отчёта */
const CONVEYOR_ITEMS = [
  { Icon: Package, text: 'Товары и каталог' },
  { Icon: Factory, text: 'Производство' },
  { Icon: ShoppingCart, text: 'Заказы и обработка' },
  { Icon: Users, text: 'Услуги и специалисты' },
  { Icon: Receipt, text: 'Оплата и документы' },
  { Icon: Truck, text: 'Отгрузка и трекинг' },
  { Icon: MapPin, text: 'Доставка и ПВЗ' },
  { Icon: QrCode, text: 'QR-коды и маркировка' },
  { Icon: Warehouse, text: 'Склады и инвентаризация' },
  { Icon: Globe, text: 'Каналы продаж' },
  { Icon: UserCog, text: 'Сотрудники и смены' },
  { Icon: BarChart3, text: 'Контроль и аналитика' },
];

/* AI-помощники: карточка = готовый вопрос в чат */
const AI_WORKSHOP_CARDS = [
  {
    Icon: PackageSearch,
    title: 'Карточки товаров',
    text: 'Соберёт описание, категорию и характеристики из вашего прайса — вместо ручного набора.',
    prompt: 'Собери карточку товара по строке из моего прайса: название, описание, категория, характеристики.',
  },
  {
    Icon: MessageSquare,
    title: 'Ответы клиентам',
    text: 'Подскажет менеджеру ответ прямо в переписке — со сроками, ценой и остатком по заказу.',
    prompt: 'Клиент спрашивает про сроки доставки и наличие. Как Markbase поможет менеджеру ответить быстрее?',
  },
  {
    Icon: Palette,
    title: 'Тексты и картинки',
    text: 'Напишет описание для витрины, пост для соцсетей и подберёт изображение под товар.',
    prompt: 'Напиши продающее описание товара для витрины и короткий пост для соцсетей.',
  },
  {
    Icon: LineChart,
    title: 'Разбор цифр',
    text: 'Объяснит человеческим языком, что выросло, что просело и где залежался товар.',
    prompt: 'Покажи, как AI объясняет продажи и остатки: что выросло, что просело, где залежался товар.',
  },
  {
    Icon: ScrollText,
    title: 'Договоры и документы',
    text: 'Прочитает договор, вытащит сроки и штрафы, предложит деловую формулировку ответа.',
    prompt: 'Разбери договор поставки: какие сроки, штрафы и на что обратить внимание.',
  },
  {
    Icon: Bot,
    title: 'Бот в Telegram',
    text: 'Тот же помощник отвечает клиентам там, где им удобно, и приносит заявки в кабинет.',
    prompt: 'Хочу бота в Telegram, который принимает заявки и передаёт их в заказы. Как это устроено?',
  },
];

/* Быстрый вход: три шага до первого результата */
const QUICKSTART_CARDS = [
  {
    step: '01',
    Icon: Package,
    badge: 'Каталог',
    title: 'Товары, услуги, цифровое',
    text: 'Excel, CSV, XML или список в письме. AI собирает карточки — вы правите готовое, каталог не набираете руками.',
    prompt: 'Хочу завести каталог товаров, услуг и цифровых позиций. У меня файл — как загрузить его в Markbase?',
  },
  {
    step: '02',
    Icon: Users,
    badge: 'Кабинеты',
    title: 'ЛК и рабочее место',
    text: 'Покупатель видит заказы и записи в своём ЛК. Сотрудники работают в кабинете с правами — без общей свалки доступов.',
    prompt: 'Как в Markbase устроены личный кабинет покупателя и рабочее место сотрудников?',
  },
  {
    step: '03',
    Icon: Smartphone,
    badge: 'Каналы',
    title: 'Сайт, Telegram, MAX, VK',
    text: 'Несколько витрин из одного каталога. Заказы с сайта и из мессенджеров приходят в один кабинет.',
    prompt: 'Хочу свою витрину на домене и каналы Telegram, MAX, VK из одного каталога. С чего начать?',
  },
];

/* Интеграции: что клиент получает, а не как это включается */
const INTEGRATION_CARDS = [
  {
    Icon: CreditCard,
    title: 'Приём оплаты',
    text: 'Карты и счета прямо из заказа. Чек уходит покупателю, оплата сразу видна менеджеру.',
  },
  {
    Icon: FileText,
    title: 'Счета и закрывающие',
    text: 'Счёт, накладная и акт собираются из состава заказа. Подписание — в электронном виде.',
  },
  {
    Icon: Truck,
    title: 'Доставка и ПВЗ',
    text: 'Службы доставки, пункты выдачи и трек-номер. Клиент видит статус, вы — где посылка.',
  },
  {
    Icon: Store,
    title: 'Каналы продаж',
    text: 'Витрина, маркетплейсы и мессенджеры берут один каталог. Заказы возвращаются в кабинет.',
  },
];

/* Безопасность человеческим языком: что это значит для владельца бизнеса */
const SECURITY_CARDS = [
  {
    Icon: Building2,
    title: 'Ваши данные не смешиваются с чужими',
    text: 'Каждая компания работает в своём контуре: товары, клиенты и заказы соседей вам не видны, а ваши — им.',
  },
  {
    Icon: FileCheck2,
    title: 'Согласия и документы в одном месте',
    text: 'Согласия покупателей, договоры и закрывающие хранятся вместе с заказом — не в чужих папках и мессенджерах.',
  },
  {
    Icon: KeyRound,
    title: 'Ключи маркетплейсов остаются на сервере',
    text: 'Доступы к площадкам и платёжным сервисам не попадают в браузер и не видны сотрудникам без прав.',
  },
  {
    Icon: EyeOff,
    title: 'Витрина не отдаёт внутреннее',
    text: 'Покупатель видит товар и свой заказ. Закупочные цены, остатки складов и данные других клиентов — нет.',
  },
];

/* Что входит в бесплатный старт */
const PRICING_INCLUDED = [
  'Кабинет компании, каталог товаров и заказы',
  '30 дней на все модули — склад, доставка, документы, CRM',
  'AI-помощник и импорт прайсов без доплат',
];

const PRICING_AI_PROMPT =
  'Подскажи тариф: у нас небольшой магазин, нужен каталог, заказы и доставка. Что войдёт в бесплатный старт и когда придётся платить?';

/* FAQ: аккордеон, первый вопрос открыт */
const FAQ_ITEMS = [
  {
    id: 'what',
    q: 'Что такое Markbase простыми словами?',
    a: 'Один кабинет для торговли: товары, заказы, склад, доставка, документы, клиенты и сотрудники работают на одних данных. Вместо десятка программ и таблиц — одна цепочка, в которой AI берёт на себя рутину: импорт прайсов, карточки товаров, ответы клиентам.',
  },
  {
    id: 'start-small',
    q: 'Можно начать только с магазина?',
    a: 'Да, это самый частый сценарий. Заводите каталог и витрину, работаете. Склад, доставку, документы, CRM и маркетплейсы подключаете позже — данные уже на месте, переезжать второй раз не придётся.',
  },
  {
    id: 'channels-vitrinas',
    q: 'Можно несколько витрин — сайт, Telegram, MAX, VK?',
    a: 'Да. У компании несколько витрин: свой сайт и домен, Telegram, MAX, VK и другие каналы. Каталог один — товары, услуги и цифровые позиции заводятся один раз, заказы приходят в один кабинет. Покупатель получает свой ЛК, сотрудники — рабочее место с правами.',
  },
  {
    id: 'existing-site',
    q: 'У нас уже есть сайт — что делать с ним?',
    a: 'Сайт можно оставить. Markbase станет источником товаров и заказов, а обмен настраивается через API или файлы. Многие сначала переносят учёт, а витрину меняют позже — когда убедятся, что данные сходятся.',
  },
  {
    id: 'accounting',
    q: 'Вы заменяете 1С и бухгалтерию?',
    a: 'Нет, и не обещаем. Мы закрываем торговую цепочку: каталог, заказы, склад, доставку, первичные документы. Бухгалтерия остаётся у вашего бухгалтера — с ней мы стыкуемся выгрузками и документами.',
  },
  {
    id: 'duplicates',
    q: 'Прайсы от разных поставщиков — не будет дублей?',
    a: 'Перед слиянием строки нормализуются и сопоставляются по артикулу, штрихкоду и названию. Спорные позиции AI показывает отдельно: вы подтверждаете, а не ищете дубли вручную по всей базе.',
  },
  {
    id: 'ai-scope',
    q: 'AI видит все наши данные?',
    a: 'Только то, что доступно вашей компании и конкретному сотруднику по его правам и тарифу. Чужие компании помощнику не видны, а ключи интеграций остаются на сервере и в ответы не попадают.',
  },
  {
    id: 'chat-same-ai',
    q: 'Чат на этой странице — это тот же AI?',
    a: 'Да, тот же помощник, что работает внутри платформы. Здесь он отвечает на вопросы о продукте, помогает прикинуть тариф и подсказывает, с чего начать именно вашему бизнесу.',
  },
  {
    id: 'chat-no-signup',
    q: 'Можно спросить без регистрации?',
    a: 'Да, несколько вопросов доступны сразу — просто напишите в чат вверху страницы. Чтобы помощник работал с вашими товарами, файлами и заказами, понадобится бесплатный аккаунт.',
  },
  {
    id: 'chat-privacy',
    q: 'Что происходит с тем, что я пишу в чате?',
    a: 'Сообщения обрабатываются на наших серверах в России и используются только для ответа вам. Паспортные данные, пароли и реквизиты карт вводить не нужно — для ответа они не требуются.',
  },
  {
    id: 'pricing-where',
    q: 'Где посмотреть цены и условия?',
    a: 'Актуальные тарифы — на странице «Тарифы», договор и политика обработки данных — в подвале сайта. Если не хочется читать всё, спросите в чате: помощник подскажет, какой тариф закроет ваш объём.',
  },
];

export default function LandingPage() {
  useSEO('landing');
  const [openFaqId, setOpenFaqId] = useState(FAQ_ITEMS[0].id);
  const [chatHintIndex, setChatHintIndex] = useState(0);
  useEffect(() => {
    const hintInterval = setInterval(() => {
      setChatHintIndex((index) => (index + 1) % HERO_CHAT_HINTS.length);
    }, 3200);
    const wrap = document.querySelector('.mb-landing-wrap');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const onPointer = (event) => {
      if (!wrap || reduceMotion) return;
      wrap.style.setProperty('--lp-mx', `${event.clientX}px`);
      wrap.style.setProperty('--lp-my', `${event.clientY}px`);
    };
    if (!reduceMotion) {
      window.addEventListener('pointermove', onPointer, { passive: true });
    }
    /* ═══ Анимация слов в Hero ═══ */
    const words = document.querySelectorAll('.hero__word');
    let current = 0;
    const wordInterval = setInterval(() => {
      if (words.length === 0) return;
      words[current].classList.remove('active');
      current = (current + 1) % words.length;
      words[current].classList.add('active');
    }, 1540);

    /* ═══ Fortress scroll animation ═══ */
    const builderSection = document.querySelector('.ecosystem-builder');
    const items = document.querySelectorAll('.eb-scatter-item');
    const fortress = document.querySelector('.eb-city');
    const glow = document.querySelector('.eb-glow');
    const shield = document.querySelector('.eb-shield');
    const ebFloor = document.querySelector('.eb-floor');
    const bg = document.querySelector('.ecosystem-builder__bg');

    const videoExts = ['mp4', 'webm', 'mov'];

    // Object.create(null) — иначе data-module="constructor" читает Object.prototype.constructor → мусорный URL иконки.
    const iconMap = Object.assign(Object.create(null), {
      market: 'Market', hrm: 'HRM', shop: 'Shop', orders: 'Orders',
      crm: 'CRM', wms: 'WMS', delivery: 'Delivery',
      integrations: 'Integrations', logistics: 'Logistics',
      files: 'Files', search: 'Search', finance: 'Finance',
      analytics: 'Analytics', bonus: 'Bonus', feed: 'Feed',
      assistant: 'Assistant', estimates: 'Estimates', app: 'App',
      chats: 'Chats', kassa: 'Kassa', documents: 'Documents',
      power: 'Power', legal: 'Legal', support: 'Support',
      services: 'Services', torg: 'Torg', waygen: 'Waygen',
      waygpt: 'Waygpt', constructor: 'Constructor', mplaza: 'Mplaza',
      notifications: 'Notifications', news: 'News', compliance: 'Compliance',
      workspace: 'Workspace', widgets: 'WidgetSystem', fab3d: 'Fab3d',
      uam: 'Uam', billing: 'Billing', wallet: 'Wallet', security: 'Security',
    });

    function makeMedia(src, alt) {
      const ext = src.split('.').pop().toLowerCase();
      if (videoExts.includes(ext)) {
        return `<video src="${src}" autoplay muted loop playsinline></video>`;
      }
      return `<img src="${src}" alt="${alt || ''}" decoding="async" onerror="this.style.display='none'" />`;
    }

    let openBrickExpand = null;
    let openModalBrick = null;

    function isMobileBrickLayout() {
      return window.matchMedia('(max-width: 768px)').matches;
    }

    function closeBrickModal() {
      if (openBrickExpand) {
        openBrickExpand.classList.remove('is-open');
        openBrickExpand = null;
      }
      if (openModalBrick) {
        openModalBrick.classList.remove('eb-brick--modal-open');
        openModalBrick = null;
      }
      document.body.style.overflow = '';
    }

    const onKeyDown = (e) => {
      if (e.key === 'Escape' && isMobileBrickLayout()) closeBrickModal();
    };
    document.addEventListener('keydown', onKeyDown);

    function staggeredHouseP(p, houseEl) {
      if (!houseEl || !houseEl.classList.contains('eb-house')) return p;
      let phase = -1;
      if (houseEl.classList.contains('eb-house--trade')) phase = 0;
      else if (houseEl.classList.contains('eb-house--ops')) phase = 1;
      else if (houseEl.classList.contains('eb-house--mgmt')) phase = 2;
      else if (houseEl.classList.contains('eb-house--core')) phase = 3;
      if (phase < 0) return p;
      const narrow = window.innerWidth <= 768;
      if (narrow && phase === 0) {
        const bandTrade = 0.10;
        return Math.min(1, Math.max(0, p / bandTrade));
      }
      if (narrow && phase === 1) {
        const bandOps = 0.36;
        return Math.min(1, Math.max(0, p / bandOps));
      }
      const overlap = 0.07;
      const band = 0.5;
      const start = phase * overlap;
      return Math.min(1, Math.max(0, (p - start) / band));
    }

    /* Создание карточек кирпичей и медиа-слоев */
    const portalExpandEls = [];
    document.querySelectorAll('.eb-brick[data-module]').forEach(brick => {
      const mod = brick.dataset.module;
      const desc = brick.dataset.desc;
      const iconEl = brick.querySelector('.eb-brick__icon');
      const name = brick.querySelector('.eb-brick__text')?.textContent || '';
      const emoji = iconEl?.textContent || '';
      const baseName = iconMap[mod];

      let brickMediaHtml = `<span>${emoji}</span>`;
      let expandMediaHtml = `<span>${emoji}</span>`;

      if (baseName) {
        const whiteSrc = `${ICON_BASE}/white/${baseName}_white.jpg`;
        const darkSrc = `${ICON_BASE}/dark/${baseName}_dark.jpg`;
        const whiteMedia = makeMedia(whiteSrc, name);
        const darkMedia = makeMedia(darkSrc, name);

        const combinedHtml = `
          <div class="eb-media-layer eb-media-light">${whiteMedia}</div>
          <div class="eb-media-layer eb-media-dark">${darkMedia}</div>
        `;
        brickMediaHtml = combinedHtml;
        expandMediaHtml = combinedHtml;
      }

      if (iconEl) iconEl.innerHTML = brickMediaHtml;

      const expand = document.createElement('div');
      expand.className = 'eb-brick__expand';

      expand.innerHTML = `
        <div class="eb-brick__expand-icon">${expandMediaHtml}</div>
        <div class="eb-brick__expand-content">
          <div class="eb-brick__expand-name">${name}</div>
          <div class="eb-brick__expand-desc">${desc || ''}</div>
          <a class="eb-brick__expand-btn" href="${resolveModuleUrl(MODULE_ROUTES[mod])}">Подробнее →</a>
        </div>
      `;
      if (isMobileBrickLayout()) {
        expand.classList.add('eb-brick__expand--portal');
        expand.setAttribute('role', 'dialog');
        expand.setAttribute('aria-modal', 'true');
        document.body.appendChild(expand);
        portalExpandEls.push(expand);
      } else {
        brick.appendChild(expand);
      }

      /* Деревянная фактура на кирпиче */
      function generateWoodGrainSVG() {
        const n = 9 + Math.floor(Math.random() * 5);
        let paths = '';
        let y = 4 + Math.random() * 2;
        for (let i = 0; i < n && y < 64; i++) {
          const w = () => (Math.random() - 0.5) * 1.8;
          const y1 = y + w(), y2 = y + w(), y3 = y + w();
          const thick = Math.random() > 0.82 ? 1.6 + Math.random() * 0.9 : 0.4 + Math.random() * 0.7;
          let stroke;
          if (Math.random() > 0.78) stroke = 'rgba(15,8,5,0.35)';
          else if (Math.random() > 0.88) stroke = 'rgba(230,200,150,0.12)';
          else stroke = `rgba(${55 + Math.floor(Math.random() * 45)},${35 + Math.floor(Math.random() * 30)},${18 + Math.floor(Math.random() * 15)},${0.12 + Math.random() * 0.22})`;
          const d = `M0 ${y1.toFixed(1)} C ${40 + Math.random() * 25} ${(y + Math.random()).toFixed(1)}, ${100 + Math.random() * 30} ${(y + Math.random() * 1.2).toFixed(1)}, ${160 + Math.random() * 20} ${y2.toFixed(1)} S 210 ${(y + Math.random()).toFixed(1)}, 240 ${y3.toFixed(1)}`;
          paths += `<path d="${d}" fill="none" stroke="${stroke}" stroke-width="${thick.toFixed(2)}" stroke-linecap="round"/>`;
          y += 1.8 + Math.random() * 3.5;
        }
        const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 68' preserveAspectRatio='none' width='240' height='68'>${paths}</svg>`;
        return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
      }

      const darkLighter = (Math.random() - 0.5) * 0.08;
      const overlay = darkLighter > 0 ? `rgba(255,255,255,${darkLighter})` : `rgba(0,0,0,${-darkLighter})`;
      const woodGrain = generateWoodGrainSVG();

      const house = brick.closest('.eb-house');
      let woodBase = 'linear-gradient(180deg, #2a2a2a 0%, #141414 100%),';
      if (house?.classList.contains('eb-house--trade')) {
        woodBase = `
          linear-gradient(168deg, rgba(255,210,170,0.14) 0%, transparent 38%),
          linear-gradient(180deg, #4e2e1c 0%, #2c160e 55%, #1a0c08 100%),`;
      } else if (house?.classList.contains('eb-house--core')) {
        woodBase = `
          linear-gradient(165deg, rgba(255,200,120,0.1) 0%, transparent 40%),
          linear-gradient(180deg, #3a2810 0%, #1a1208 100%),`;
      } else if (house?.classList.contains('eb-house--ops')) {
        woodBase = 'linear-gradient(180deg, #383634 0%, #181614 100%),';
      } else if (house?.classList.contains('eb-house--mgmt')) {
        woodBase = 'linear-gradient(180deg, #2a3844 0%, #121a20 100%),';
      }

      brick.style.backgroundImage = `
        linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 40%, rgba(0,0,0,0.18) 100%),
        ${woodGrain},
        ${woodBase}
        linear-gradient(${overlay}, ${overlay})
      `;

      if (isMobileBrickLayout()) {
        brick.addEventListener('click', (e) => {
          if (!fortress.classList.contains('eb-city--center-on') && !fortress.classList.contains('completed')) return;
          e.preventDefault();
          e.stopPropagation();
          if (openBrickExpand === expand && expand.classList.contains('is-open')) {
            closeBrickModal();
            return;
          }
          closeBrickModal();
          openBrickExpand = expand;
          openModalBrick = brick;
          expand.classList.add('is-open');
          brick.classList.add('eb-brick--modal-open');
        });
      }
    });

    /* Амплитуда разлёта */
    function scatterAmpScale() {
      const m = Math.min(window.innerWidth, window.innerHeight);
      if (m >= 960) return 1;
      return Math.max(0.2, Math.min(0.48, m / 1100));
    }
    const _scatterAmp = scatterAmpScale();
    const scatterFromHeroRight = window.innerWidth > 768;

    items.forEach((item, index) => {
      const animType = Math.floor(Math.random() * 4);
      item._animType = animType;
      item._sScale = 1;
      item._sBlur = 0;

      if (scatterFromHeroRight) {
        const rnd = (a, b) => a + Math.random() * (b - a);
        const layer = (index % 7) * 0.028;
        item._sx = 260 + layer * 80 + rnd(32, 340);
        item._sy = rnd(-520, -120);
        item._sr = rnd(-40, 40);
        item._sBlur = 10 + rnd(0, 12);
        item._sScale = 0.8 + rnd(0, 0.2);
        if (animType === 1) {
          item._sy -= rnd(80, 220);
          item._sx += rnd(16, 90);
          item._sBlur += rnd(2, 9);
        } else if (animType === 2) {
          item._sScale = 0.66 + rnd(0, 0.14);
          item._sy = rnd(-480, -100);
          item._sBlur += rnd(4, 12);
        } else if (animType === 3) {
          item._sy += rnd(-90, 100);
          item._sx += rnd(50, 200);
        }
      } else if (animType === 0) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 500 + Math.random() * 500;
        item._sx = Math.cos(angle) * dist;
        item._sy = Math.sin(angle) * dist;
        item._sr = (Math.random() - 0.5) * 80;
      } else if (animType === 1) {
        item._sx = (Math.random() - 0.5) * 400;
        item._sy = -800 - Math.random() * 600;
        item._sr = (Math.random() - 0.5) * 45;
        item._sBlur = 10 + Math.random() * 10;
        item._sScale = 1.3 + Math.random() * 0.5;
      } else if (animType === 2) {
        item._sx = (Math.random() - 0.5) * 150;
        item._sy = (Math.random() - 0.5) * 150;
        item._sr = (Math.random() - 0.5) * 180;
        item._sBlur = 20;
        item._sScale = 0.1;
      } else if (animType === 3) {
        const side = Math.random() > 0.5 ? 1 : -1;
        item._sx = side * (500 + Math.random() * 400);
        item._sy = 600 + Math.random() * 400;
        item._sr = side * (45 + Math.random() * 45);
        item._sBlur = 5;
        item._sScale = 0.7;
      }

      if (item.classList.contains('eb-roof') || item.classList.contains('eb-foundation')) {
        item._sBlur = scatterFromHeroRight ? 4 + Math.random() * 3.5 : 0;
      }

      const amp = _scatterAmp;
      item._sx *= amp;
      item._sy *= amp;
      item._sr *= Math.sqrt(amp);
      if (item._sBlur > 0) {
        item._sBlur = Math.min(14, item._sBlur * amp * 1.15);
      }

      item.style.transform = `translate(${item._sx}px, ${item._sy}px) rotate(${item._sr}deg) scale(${item._sScale})`;
      if (item._sBlur > 0) item.style.filter = `blur(${item._sBlur}px)`;
      item.style.opacity = '0';
    });

    let isCompleted = false;

    const onScroll = () => {
      if (!builderSection) return;
      const rect = builderSection.getBoundingClientRect();
      const wh = window.innerHeight;
      const sectionInView = rect.bottom > 0 && rect.top < wh;

      if (!sectionInView) {
        closeBrickModal();
        items.forEach(b => {
          b.style.transform = `translate(${b._sx}px, ${b._sy}px) rotate(${b._sr}deg) scale(${b._sScale})`;
          if (b._sBlur > 0) b.style.filter = `blur(${b._sBlur}px)`;
          else b.style.removeProperty('filter');
          b.style.opacity = '0';
          b.style.visibility = 'hidden';
          b.style.pointerEvents = 'none';
        });
        if (fortress) fortress.classList.remove('eb-city--center-on');
        if (fortress) fortress.style.setProperty('--floors-o', '0');
        if (isCompleted) {
          isCompleted = false;
          if (fortress) fortress.classList.remove('completed');
          if (glow) glow.style.opacity = '0';
          if (shield) shield.style.opacity = '0';
          if (ebFloor) ebFloor.style.opacity = '0';
          if (bg) bg.classList.remove('focused');
        }
        return;
      }

      const narrow = window.innerWidth <= 768;
      const H = Math.max(1, rect.height);
      const visible = Math.max(0, Math.min(rect.bottom, wh) - Math.max(rect.top, 0));
      const visThreshold = 0.3 * wh;
      const endTop = wh - H;
      const startTopEff = wh - visThreshold;
      const span = Math.max(1e-4, startTopEff - endTop);
      let rawLinear;
      if (rect.top <= endTop) {
        rawLinear = 1;
      } else if (visible + 1e-3 < visThreshold) {
        rawLinear = 0;
      } else {
        rawLinear = Math.min(1, Math.max(0, (startTopEff - rect.top) / span));
      }
      const smoothstep = (t) => {
        const x = Math.min(1, Math.max(0, t));
        return x * x * (3 - 2 * x);
      };
      const p = smoothstep(rawLinear);
      const floorsO = Math.min(1, Math.max(0, (p - 0.32) / 0.58));
      if (fortress) fortress.style.setProperty('--floors-o', floorsO.toFixed(4));

      const blurCap = narrow ? 5 : 11;
      const completeEnter = narrow ? 0.82 : 0.94;
      const completeExit = narrow ? 0.58 : 0.78;
      const centerOnByP = p > (narrow ? 0.02 : 0.06);
      const centerOnByGeom =
        narrow &&
        rect.top < wh * 0.9 &&
        rect.bottom > wh * 0.28;
      if (fortress) {
        if (centerOnByP || centerOnByGeom) {
          fortress.classList.add('eb-city--center-on');
        } else {
          fortress.classList.remove('eb-city--center-on');
        }
      }

      items.forEach(b => {
        b.style.visibility = 'visible';
        b.style.pointerEvents = '';

        const houseEl = b.closest('.eb-house');
        const pItem = staggeredHouseP(p, houseEl);

        const invP = 1 - pItem;
        const tx = b._sx * invP;
        const ty = b._sy * invP;
        const tr = b._sr * invP;
        const tScale = b._sScale * invP + 1 * pItem;
        const blurFalloff = Math.pow(Math.max(0, invP), 0.42);
        const tBlur = Math.min(blurCap, (b._sBlur || 0) * blurFalloff);

        b.style.transform = `translate(${tx}px, ${ty}px) rotate(${tr}deg) scale(${tScale})`;
        if (tBlur > 0.35) {
          b.style.filter = `blur(${tBlur}px)`;
        } else {
          b.style.removeProperty('filter');
        }

        const opacityMul = narrow ? 2.2 : 2.75;
        let opacity = Math.min(1, Math.max(0, Math.pow(pItem, 0.78) * opacityMul));
        if (pItem < 0.97) opacity = Math.min(opacity, 0.93);
        b.style.opacity = opacity.toString();
      });

      if (p > completeEnter) {
        if (!isCompleted) {
          isCompleted = true;
          if (fortress) fortress.classList.add('completed');
          if (glow) glow.style.opacity = '1';
          if (shield) shield.style.opacity = '1';
          if (ebFloor) ebFloor.style.opacity = '1';
          if (bg) bg.classList.add('focused');
        }
      } else if (p < completeExit) {
        if (isCompleted) {
          isCompleted = false;
          if (fortress) fortress.classList.remove('completed');
          if (glow) glow.style.opacity = '0';
          if (shield) shield.style.opacity = '0';
          if (ebFloor) ebFloor.style.opacity = '0';
          if (bg) bg.classList.remove('focused');
        }
      }
    };

    window.addEventListener('scroll', onScroll);
    window.dispatchEvent(new Event('scroll'));

    /* ═══ Intersection Observer для секций ═══ */
    const revealTargets = document.querySelectorAll('.capabilities, .conveyor, .growth, .ai-workshop, .lp-reveal');
    let sectionObserver = null;
    if (typeof IntersectionObserver === 'undefined') {
      revealTargets.forEach((sec) => sec.classList.add('is-visible'));
    } else {
      sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      }, { threshold: 0.08, rootMargin: '80px 0px 40px 0px' });
      revealTargets.forEach((sec) => sectionObserver.observe(sec));
    }

    return () => {
      clearInterval(hintInterval);
      clearInterval(wordInterval);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('keydown', onKeyDown);
      if (sectionObserver) sectionObserver.disconnect();
      portalExpandEls.forEach(el => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, []);

  return (
    <div className="mb-landing-wrap" style={LANDING_PAGE_CSS_VARS}>
      <PublicHeader />
      <LandingSideToc />

      {/* ═══════════════ БЛОК #1 — HERO ═══════════════ */}
      <section id="top" className="hero">
        <div className="hero__bg">
          <LandingOptionalImage
            src={`${lpFile('b01_hero_main.jpg')}?v=isometric`}
            alt=""
            className="hero__bg-photo"
            loading="eager"
            preferLight
          />
        </div>
        <div className="hero__content">
          <div className="hero__brand">
            <div className="hero__logo">
              <LogoImage
                sources={publicHeaderLogoSources(PLATFORM_BRANDING_SITE_LOGO)}
                alt="Маркбэйс"
                height={58}
                maxWidth={220}
                style={{ marginRight: 0 }}
              />
              <div className="hero__logo-text">МАРКБЭЙС</div>
            </div>
            <a className="hero__badge" href="#ecosystem">Проекты · услуги · платформа</a>
          </div>
          <h1 className="hero__heading">
            <span className="hero__heading-static">Скажите задачу —</span>
            <span className="hero__heading-rotate">
              <span className="hero__word active">СОБЕРЁТ ВИТРИНУ</span>
              <span className="hero__word">ЗАГРУЗИТ ТОВАРЫ</span>
              <span className="hero__word">ОТКРОЕТ ЗАПИСЬ</span>
              <span className="hero__word">ВЫДАСТ ЦИФРОВОЕ</span>
              <span className="hero__word">ПОДКЛЮЧИТ КАНАЛЫ</span>
            </span>
          </h1>
          <button type="button" className="hero__chat" onClick={() => openLandingAgent()}>
            <span className="hero__chat-icon" aria-hidden>
              <Sparkles size={20} strokeWidth={2.2} />
            </span>
            <span className="hero__chat-hint">{HERO_CHAT_HINTS[chatHintIndex]}</span>
            <span className="hero__chat-go">Спросить</span>
          </button>
          <div className="hero__metrics">
            <div className="hero__metric">
              <div className="hero__metric-value">37</div>
              <div className="hero__metric-label">модулей</div>
            </div>
            <div className="hero__metric">
              <div className="hero__metric-value">20<span>+</span></div>
              <div className="hero__metric-label">AI-помощников</div>
            </div>
            <div className="hero__metric">
              <div className="hero__metric-value">5</div>
              <div className="hero__metric-label">направлений бизнеса</div>
            </div>
            <div className="hero__metric">
              <div className="hero__metric-value">RU</div>
              <div className="hero__metric-label">серверы в России</div>
            </div>
          </div>
          <div className="hero__cta">
            <button type="button" className="btn-primary" onClick={() => openLandingAgent()}>
              Спросить AI{' '}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
              </svg>
            </button>
            <a href={APP_REGISTER_URL()} className="btn-secondary">Начать бесплатно</a>
          </div>
        </div>
      </section>

      <LandingEcosystem />

      {/* ═══════════════ ЛЕНТА ВОЗМОЖНОСТЕЙ ═══════════════ */}
      <section className="lp-badge-strip" aria-label="Возможности платформы">
        <div className="lp-badge-strip__inner">
          <div className="lp-badge-strip__marquee">
            <div className="lp-badge-strip__track">
              <ul className="lp-badge-strip__list">
                {HERO_CAPABILITY_BADGES.map((badge) => (
                  <li className="lp-badge-strip__item" key={badge}>{badge}</li>
                ))}
              </ul>
              <ul className="lp-badge-strip__list lp-badge-strip__list--dup" aria-hidden="true">
                {HERO_CAPABILITY_BADGES.map((badge) => (
                  <li className="lp-badge-strip__item" key={badge}>{badge}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="lp-badge-strip__note">Всё это — в одном кабинете и на одних данных.</p>
        </div>
      </section>

      {/* ═══════════════ КОМУ ПОДХОДИТ — split: сетка слева, кадр справа ═══════════════ */}
      <section id="who" className="lp-split lp-who lp-reveal">
        <div className="lp-split__inner">
          <div className="lp-split__copy">
            <div className="lp-split__kicker">Кому подходит</div>
            <h2 className="lp-split__title">Товары, услуги и цифровое — в ваших витринах и кабинетах</h2>
            <p className="lp-split__lead">
              Каждый клиент собирает свой контур: каталог, витрины, личный кабинет покупателя и рабочее место сотрудников.
            </p>
            <div className="lp-split__lib">
              {WHO_CARDS.map(({ id, Icon, title, text, prompt }) => (
                <button
                  type="button"
                  className="lp-lib-item"
                  key={id}
                  onClick={() => openLandingAgent(prompt)}
                >
                  <span className="lp-lib-item__icon" aria-hidden>
                    <Icon size={28} strokeWidth={2.1} />
                  </span>
                  <b>{title}</b>
                  <span>{text}</span>
                </button>
              ))}
            </div>
          </div>
          <figure className="lp-split__visual">
            <LandingOptionalImage
              src={`${lpFile('b18_capabilities_village.jpg')}?v=village`}
              fallbackSrc={lpFile('b01_hero_main.jpg')}
              alt=""
              className="lp-split__visual-photo"
              preferLight
            />
            <span className="lp-split__chip lp-split__chip--a">Витрина</span>
            <span className="lp-split__chip lp-split__chip--b">ЛК покупателя</span>
            <span className="lp-split__chip lp-split__chip--c">Кабинет работы</span>
          </figure>
        </div>
      </section>

      {/* ═══════════════ AI ЗА ВАС — split reverse ═══════════════ */}
      <section id="ai-flow" className="lp-split lp-split--reverse lp-ai-flow">
        <div className="lp-split__inner">
          <div className="lp-split__copy">
            <div className="lp-split__kicker">AI за вас</div>
            <h2 className="lp-split__title">Опишите задачу — дальше сделает Markbase</h2>
            <p className="lp-split__lead">
              Каталог, витрины и кабинеты собираются из вашего файла и диалога.
            </p>
            <div className="lp-split__lib">
              {AI_FLOW_CARDS.map((card) => (
                <button
                  type="button"
                  className="lp-lib-item"
                  key={card.id}
                  onClick={() => openLandingAgent(card.prompt)}
                >
                  <span className="lp-lib-item__icon" aria-hidden>
                    <card.Icon size={28} strokeWidth={2.1} />
                  </span>
                  <b>{card.title}</b>
                  <span>{card.steps[0]}</span>
                </button>
              ))}
            </div>
          </div>
          <figure className="lp-split__visual">
            <LandingOptionalImage
              src={`${lpFile('b01_hero_main2.jpg')}?v=village`}
              fallbackSrc={lpFile('b01_hero_main.jpg')}
              alt=""
              className="lp-split__visual-photo"
              preferLight
            />
            <span className="lp-split__chip lp-split__chip--a">Каталог</span>
            <span className="lp-split__chip lp-split__chip--b">Тариф</span>
            <span className="lp-split__chip lp-split__chip--c">Каналы</span>
          </figure>
        </div>
      </section>

      <section id="modules" className="ecosystem-builder">
        <div className="ecosystem-builder__sticky">
          <div className="ecosystem-builder__bg">
            <LandingOptionalImage src={lpFile('b02_fortress_yard_bg.jpg')} alt="" className="ecosystem-builder__bg-photo" />
          </div>

          <div className="ecosystem-builder__scene">
            <div className="eb-glow"></div>

            <div className="eb-architecture">
              <div className="eb-city">
                <div className="eb-shield"></div>
                <div className="eb-floor"></div>
                <div className="eb-connection-lines"></div>

                {/* ТОРГОВЛЯ */}
                <div className="eb-house eb-house--trade eb-house--6">
                  <div className="eb-roof eb-roof--trade eb-scatter-item"></div>
                  <div className="eb-house-floors">
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="shop" data-desc="Магазин"><div className="eb-brick__icon">🛒</div><span className="eb-brick__text">Магазин</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="market" data-desc="Маркетплейс"><div className="eb-brick__icon">📈</div><span className="eb-brick__text">Маркетплейс</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="orders" data-desc="Заказы"><div className="eb-brick__icon">📦</div><span className="eb-brick__text">Заказы</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="crm" data-desc="Клиенты"><div className="eb-brick__icon">👥</div><span className="eb-brick__text">Клиенты</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="delivery" data-desc="Доставка"><div className="eb-brick__icon">🚚</div><span className="eb-brick__text">Доставка</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="seo" data-desc="Продвижение"><div className="eb-brick__icon">🔍</div><span className="eb-brick__text">Продвижение</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="bonus" data-desc="Бонусы"><div className="eb-brick__icon">🎁</div><span className="eb-brick__text">Бонусы</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="loyalty" data-desc="Лояльность"><div className="eb-brick__icon">⭐</div><span className="eb-brick__text">Лояльность</span></div>
                    </div>
                  </div>
                  <div className="eb-foundation eb-foundation--trade eb-scatter-item"><span className="eb-foundation__txt">ТОРГОВЛЯ</span></div>
                </div>

                {/* ОПЕРАЦИИ */}
                <div className="eb-house eb-house--ops eb-house--9">
                  <div className="eb-roof eb-roof--ops eb-scatter-item"></div>
                  <div className="eb-house-floors">
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="wms" data-desc="Склад"><div className="eb-brick__icon">🏭</div><span className="eb-brick__text">Склад</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="logistics" data-desc="Логистика"><div className="eb-brick__icon">🔄</div><span className="eb-brick__text">Логистика</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="constructor" data-desc="Конструктор"><div className="eb-brick__icon">🏗️</div><span className="eb-brick__text">Констр.</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="import" data-desc="Импорт"><div className="eb-brick__icon">📥</div><span className="eb-brick__text">Импорт</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="services" data-desc="Услуги"><div className="eb-brick__icon">🛠️</div><span className="eb-brick__text">Услуги</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="registries" data-desc="Реестры"><div className="eb-brick__icon">🧾</div><span className="eb-brick__text">Реестры</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="documents" data-desc="Документы"><div className="eb-brick__icon">📄</div><span className="eb-brick__text">Документы</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="reports" data-desc="Отчёты"><div className="eb-brick__icon">📝</div><span className="eb-brick__text">Отчёты</span></div>
                    </div>
                  </div>
                  <div className="eb-foundation eb-foundation--ops eb-scatter-item"><span className="eb-foundation__txt">ОПЕРАЦИИ</span></div>
                </div>

                {/* УПРАВЛЕНИЕ */}
                <div className="eb-house eb-house--mgmt eb-house--3">
                  <div className="eb-roof eb-roof--mgmt eb-scatter-item"></div>
                  <div className="eb-house-floors">
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="hrm" data-desc="Персонал"><div className="eb-brick__icon">👔</div><span className="eb-brick__text">Персонал</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="chats" data-desc="Чаты"><div className="eb-brick__icon">💬</div><span className="eb-brick__text">Чаты</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="analytics" data-desc="Аналитика"><div className="eb-brick__icon">📊</div><span className="eb-brick__text">Аналитика</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="integrations" data-desc="Интеграции"><div className="eb-brick__icon">🧩</div><span className="eb-brick__text">Интеграции</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="billing" data-desc="Финансы"><div className="eb-brick__icon">💰</div><span className="eb-brick__text">Финансы</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="tariffs" data-desc="Тарифы"><div className="eb-brick__icon">📋</div><span className="eb-brick__text">Тариф</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="files" data-desc="Хранилище"><div className="eb-brick__icon">☁️</div><span className="eb-brick__text">Облако</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="notifications" data-desc="Уведомления"><div className="eb-brick__icon">🔔</div><span className="eb-brick__text">Уведомления</span></div>
                    </div>
                  </div>
                  <div className="eb-foundation eb-foundation--mgmt eb-scatter-item"><span className="eb-foundation__txt">УПРАВЛЕНИЕ</span></div>
                </div>

                {/* ЯДРО ПЛАТФОРМЫ */}
                <div className="eb-house eb-house--core eb-house--12">
                  <div className="eb-roof eb-roof--core eb-scatter-item"></div>
                  <div className="eb-house-floors">
                    <div className="eb-row">
                      <div className="eb-brick eb-brick--wide eb-scatter-item" data-module="security" data-desc="Защита данных и контроль доступа"><div className="eb-brick__icon">🛡️</div><span className="eb-brick__text">Безопасность</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="settings" data-desc="Настройки компании"><div className="eb-brick__icon">⚙️</div><span className="eb-brick__text">Настройки</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="auth" data-desc="Безопасный вход"><div className="eb-brick__icon">🔒</div><span className="eb-brick__text">Авторизация</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="roles" data-desc="Роли пользователей"><div className="eb-brick__icon">🔑</div><span className="eb-brick__text">Роли</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="processes" data-desc="Автоматизация"><div className="eb-brick__icon">⚡</div><span className="eb-brick__text">Процессы</span></div>
                    </div>
                    <div className="eb-row">
                      <div className="eb-brick eb-scatter-item" data-module="monitoring" data-desc="Мониторинг системы"><div className="eb-brick__icon">📊</div><span className="eb-brick__text">Мониторинг</span></div>
                      <div className="eb-brick eb-scatter-item" data-module="scheduler" data-desc="Планировщик"><div className="eb-brick__icon">⏳</div><span className="eb-brick__text">Планировщик</span></div>
                    </div>
                  </div>
                  <div className="eb-foundation eb-foundation--core eb-scatter-item"><span className="eb-foundation__txt">ЯДРО ПЛАТФОРМЫ</span></div>
                </div>

                {/* МАРКБЭЙС — центральный блок */}
                <div className="eb-center-ai">
                  <div
                    className="eb-ai-central"
                    role="button"
                    tabIndex={0}
                    aria-label="Спросить AI Markbase"
                    onClick={() => openLandingAgent()}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openLandingAgent();
                      }
                    }}
                  >
                    <div className="eb-ai-central__icon" aria-hidden="true">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 88 88" fill="none">
                        <defs>
                          <linearGradient id="wai-wood" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#2a1a12"/>
                            <stop offset="35%" stopColor="#4a3220"/>
                            <stop offset="70%" stopColor="#3d2818"/>
                            <stop offset="100%" stopColor="#22160e"/>
                          </linearGradient>
                          <linearGradient id="wai-stone" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#5c5854"/>
                            <stop offset="100%" stopColor="#3a3835"/>
                          </linearGradient>
                          <linearGradient id="wai-rack" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#151210"/>
                            <stop offset="45%" stopColor="#0c0a08"/>
                            <stop offset="100%" stopColor="#1a1510"/>
                          </linearGradient>
                          <linearGradient id="wai-glass" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#1e2a26"/>
                            <stop offset="100%" stopColor="#0a0e0c"/>
                          </linearGradient>
                          <radialGradient id="wai-core" cx="40%" cy="35%" r="65%">
                            <stop offset="0%" stopColor="#3d5a52" stopOpacity="0.35"/>
                            <stop offset="70%" stopColor="#1a2824" stopOpacity="0.12"/>
                            <stop offset="100%" stopColor="#0a0e0c" stopOpacity="0"/>
                          </radialGradient>
                        </defs>
                        <path d="M4 82 Q6 78 12 79 L20 77 Q26 75 32 78 L44 76 Q52 78 58 77 L68 79 Q74 78 78 80 L84 82 L84 88 L4 88 Z" fill="url(#wai-stone)" stroke="#2a2826" strokeWidth="0.6"/>
                        <ellipse cx="22" cy="81" rx="9" ry="3.5" fill="#4a4642" opacity="0.5"/>
                        <ellipse cx="58" cy="81" rx="11" ry="3.2" fill="#45423e" opacity="0.45"/>
                        <rect x="8" y="73" width="72" height="5" rx="1.2" fill="#3d2818" stroke="#1a120c" strokeWidth="0.7"/>
                        <rect x="9" y="28" width="7" height="46" rx="1.5" fill="url(#wai-wood)" stroke="#140c08" strokeWidth="0.6"/>
                        <rect x="72" y="28" width="7" height="46" rx="1.5" fill="url(#wai-wood)" stroke="#140c08" strokeWidth="0.6"/>
                        <ellipse cx="12.5" cy="42" rx="2" ry="5" fill="#000" opacity="0.2"/>
                        <ellipse cx="75.5" cy="48" rx="2" ry="5" fill="#000" opacity="0.18"/>
                        <path d="M44 6 L74 30 L14 30 Z" fill="#3d2818" stroke="#120a06" strokeWidth="1.1" strokeLinejoin="round"/>
                        <path d="M44 10 L66 29 L22 29 Z" fill="#4a3322" opacity="0.85"/>
                        <rect x="36" y="17" width="16" height="10" rx="1" fill="#243832" stroke="#0a0604" strokeWidth="0.75"/>
                        <line x1="44" y1="17" x2="44" y2="27" stroke="#0a0604" strokeOpacity="0.5" strokeWidth="0.5"/>
                        <line x1="36" y1="22" x2="52" y2="22" stroke="#0a0604" strokeOpacity="0.5" strokeWidth="0.5"/>
                        <circle cx="40" cy="20" r="1.1" fill="#5f8a7e" fillOpacity="0.45"/>
                        <circle cx="48" cy="25" r="1.1" fill="#5f8a7e" fillOpacity="0.28"/>
                        <path d="M44 6 L44 1.5" stroke="#5f726c" strokeOpacity="0.38" strokeWidth="0.9" strokeLinecap="round"/>
                        <circle cx="44" cy="1.2" r="1.8" fill="#5f726c" fillOpacity="0.35"/>
                        <path d="M36 4 L30 1 M52 4 L58 1" stroke="#5f726c" strokeOpacity="0.28" strokeWidth="0.7" strokeLinecap="round"/>
                        <rect x="18" y="30" width="52" height="44" rx="2" fill="url(#wai-rack)" stroke="#2a1f18" strokeWidth="1"/>
                        <rect x="21" y="33" width="46" height="38" rx="1.2" fill="url(#wai-glass)" stroke="#0d0a08" strokeWidth="0.6"/>
                        <circle cx="44" cy="48" r="14" fill="url(#wai-core)"/>
                        <g opacity="0.92">
                          <rect x="24" y="36" width="40" height="7" rx="0.8" fill="#0f0d0b" stroke="#1f1812" strokeWidth="0.35"/>
                          <circle cx="28" cy="39.5" r="1" fill="#5f8a7e" fillOpacity="0.4"/>
                          <circle cx="34" cy="39.5" r="1" fill="#5f8a7e" fillOpacity="0.55"/>
                          <circle cx="40" cy="39.5" r="1" fill="#5f8a7e" fillOpacity="0.32"/>
                          <circle cx="52" cy="39.5" r="1" fill="#5f8a7e" fillOpacity="0.48"/>
                          <circle cx="58" cy="39.5" r="1" fill="#5f8a7e" fillOpacity="0.38"/>
                          <rect x="24" y="45" width="40" height="7" rx="0.8" fill="#0f0d0b" stroke="#1f1812" strokeWidth="0.35"/>
                          <circle cx="30" cy="48.5" r="1" fill="#5f8a7e" fillOpacity="0.5"/>
                          <circle cx="38" cy="48.5" r="1" fill="#5f8a7e" fillOpacity="0.35"/>
                          <circle cx="46" cy="48.5" r="1" fill="#5f8a7e" fillOpacity="0.62"/>
                          <circle cx="54" cy="48.5" r="1" fill="#5f8a7e" fillOpacity="0.4"/>
                          <rect x="24" y="54" width="40" height="7" rx="0.8" fill="#0f0d0b" stroke="#1f1812" strokeWidth="0.35"/>
                          <circle cx="32" cy="57.5" r="1" fill="#5f8a7e" fillOpacity="0.33"/>
                          <circle cx="44" cy="57.5" r="1" fill="#5f8a7e" fillOpacity="0.52"/>
                          <circle cx="56" cy="57.5" r="1" fill="#5f8a7e" fillOpacity="0.4"/>
                        </g>
                        <path d="M24 62 H64 M26 65 H62" stroke="#5f726c" strokeOpacity="0.22" strokeWidth="0.55" strokeLinecap="round"/>
                        <path d="M44 62 V68" stroke="#5f726c" strokeOpacity="0.28" strokeWidth="0.6" strokeLinecap="round"/>
                        <rect x="19" y="44" width="2.5" height="14" rx="0.4" fill="#6a5220" opacity="0.75"/>
                        <rect x="66.5" y="44" width="2.5" height="14" rx="0.4" fill="#6a5220" opacity="0.75"/>
                        <rect x="34" y="64" width="20" height="7" rx="0.6" fill="#12100e" stroke="#2a2218" strokeWidth="0.35"/>
                        <line x1="37" y1="66" x2="51" y2="66" stroke="#1a1510" strokeWidth="0.4"/>
                        <line x1="37" y1="68.5" x2="51" y2="68.5" stroke="#1a1510" strokeWidth="0.4"/>
                        <path d="M19 58 Q14 62 12 68" stroke="#3d2e20" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.85"/>
                        <path d="M69 58 Q74 62 76 68" stroke="#3d2e20" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.85"/>
                        <circle cx="12" cy="69.5" r="1.5" fill="#2a1f16"/>
                        <circle cx="76" cy="69.5" r="1.5" fill="#2a1f16"/>
                      </svg>
                    </div>
                    <div className="eb-ai-central__title">МАРКБЭЙС</div>
                    <div className="eb-ai-central__subtitle">
                      <span className="eb-ai-central__subline">Спросите AI</span>
                      <span className="eb-ai-central__subline">соберу витрину</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ БЛОК #3 — CAPABILITIES ═══════════════ */}
      <section id="capabilities" className="capabilities">
        <div className="capabilities__photo" aria-hidden>
          <LandingOptionalImage src={`${lpFile('b21_one_cabinet.jpg')}?v=village`} alt="" className="capabilities__photo-img" preferLight />
        </div>
        <div className="capabilities__glow"></div>
        <div className="capabilities__content">

          <div className="capabilities__badge capabilities-anim-item">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" fill="none" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
            Что внутри
          </div>

          <h2 className="capabilities__title capabilities-anim-item">
            Хватит сводить бизнес <span>по таблицам</span>
          </h2>
          <p className="capabilities__subtitle capabilities-anim-item">
            Один кабинет. Один каталог. AI делает рутину.
          </p>
          <div className="capabilities__cta capabilities-anim-item">
            <button type="button" className="btn-primary" onClick={() => openLandingAgent()}>
              Спросить AI{' '}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
              </svg>
            </button>
            <a className="btn-secondary" href={APP_REGISTER_URL()}>Начать бесплатно</a>
          </div>
          <Link className="capabilities__more" to="/product/waygpt">
            Как устроены помощники ВЭЙГПТ
          </Link>

        </div>
      </section>

      {/* ═══════════════ БЫСТРЫЙ ВХОД — три шага ═══════════════ */}
      <section id="quickstart" className="lp-split lp-quickstart">
        <div className="lp-split__inner">
          <div className="lp-split__copy">
            <div className="lp-split__kicker">Быстрый вход</div>
            <h2 className="lp-split__title">Три шага до первого результата</h2>
            <p className="lp-split__lead">
              Каталог, кабинеты и каналы собираются из одного кабинета — без повторного ввода и без свалки таблиц.
            </p>
            <div className="lp-steps">
              {QUICKSTART_CARDS.map((card) => (
                <button
                  type="button"
                  className="lp-step"
                  key={card.step}
                  onClick={() => openLandingAgent(card.prompt)}
                >
                  <span className="lp-step__num" aria-hidden>{card.step}</span>
                  <span className="lp-step__body">
                    <span className="lp-step__badge">
                      <card.Icon size={14} strokeWidth={2.2} aria-hidden />
                      {card.badge}
                    </span>
                    <b>{card.title}</b>
                    <span>{card.text}</span>
                  </span>
                </button>
              ))}
            </div>
            <p className="lp-split__hint">
              <strong>Один кабинет</strong> — данные заводятся один раз и работают во всех модулях.
            </p>
          </div>
          <figure className="lp-split__visual">
            <LandingOptionalImage
              src={`${lpFile('b09_catalog_upload.jpg')}?v=village`}
              fallbackSrc={lpFile('b01_hero_main.jpg')}
              alt=""
              className="lp-split__visual-photo"
              preferLight
            />
            <span className="lp-split__chip lp-split__chip--a">01 Каталог</span>
            <span className="lp-split__chip lp-split__chip--b">02 Кабинеты</span>
            <span className="lp-split__chip lp-split__chip--c">03 Каналы</span>
          </figure>
        </div>
      </section>

      {/* ═══════════════ БЛОК #4 — CONVEYOR ═══════════════ */}
      <section id="how" className="conveyor">
        <div className="conveyor__content">

          <div className="conveyor__badge conveyor-anim">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" fill="none" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
            Полный контур операций
          </div>

          <h2 className="conveyor__title conveyor-anim">
            Всё управление <span>в одном месте</span>
          </h2>

          <p className="conveyor__subtitle conveyor-anim">
            Товар, заказ, склад, доставка, оплата и документы идут одной цепочкой. Ничего не переносите руками между программами — и видите, на каком шаге стоит каждый заказ.
          </p>

          <div className="conveyor__grid conveyor-anim">
            {CONVEYOR_ITEMS.map(({ Icon, text }) => (
              <div className="conveyor__grid-item" key={text}>
                <span className="conveyor__grid-icon" aria-hidden>
                  <Icon size={17} strokeWidth={2.1} />
                </span>
                <span className="conveyor__grid-text">{text}</span>
              </div>
            ))}
          </div>

          <div className="conveyor__cta conveyor-anim">
            <button
              type="button"
              className="btn-primary"
              onClick={() => openLandingAgent('Расскажи, как в Markbase проходит путь заказа: от товара до документов и доставки.')}
            >
              Спросить, как это ляжет на ваш процесс{' '}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
              </svg>
            </button>
          </div>

        </div>

        <div className="conveyor__image-wrapper conveyor-anim">
          <div className="conveyor__fade-top"></div>
          <LandingOptionalImage src={lpFile('b04_conveyor_bottom.jpg')} alt="Полный контур операций" className="conveyor__image" />
        </div>
      </section>

      {/* ═══════════════ БЛОК #4.5 — ОДИН ИСТОЧНИК ДАННЫХ (импорт → документы) ═══════════════ */}
      <section id="data" className="landing-info-section landing-info-section--muted lp-data-flow">
        <div className="landing-info-section__inner">
          <div className="landing-info-section__label">Один источник данных</div>
          <h2 className="landing-info-section__title">Один каталог — все каналы</h2>
          <p className="landing-info-section__text">
            Разные остатки и дубли товаров появляются там, где у каждой программы своя правда. В Markbase факт один, а модули им пользуются.
          </p>
          <ol className="lp-data-flow__chain" aria-label="Путь данных: импорт, сведение, каталог, каналы, заказ, документы">
            {DATA_FLOW_STEPS.map((step, index) => (
              <li className="lp-data-flow__step" key={step.title}>
                <span className="lp-data-flow__num">{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
          <p className="lp-data-flow__note">Меняете цену один раз — она уходит во все каналы.</p>
        </div>
      </section>

      {/* ═══════════════ БЛОК #5 — GROWTH ═══════════════ */}
      <section className="growth">
        <div className="growth__content">
          <div className="growth__header-left">
            <div className="growth__badge growth-anim">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 22H3v-9l7-7.5L20 13v9h-5"/>
                <path d="M9 22V12h6v10"/>
                <path d="m14 10 3-3"/>
                <path d="m8 6 3-3"/>
              </svg>
              Эволюция бизнеса
            </div>

            <h2 className="growth__title growth-anim">
              Развивайте бизнес<br/><span>без смены основы</span>
            </h2>
          </div>

          <div className="growth__header-right">
            <p className="growth__subtitle growth-anim">
              Склады, кассы, каналы продаж и сервисы подключаются по мере роста — база остаётся той же.
            </p>

            <ul className="growth__list growth-anim">
              {[
                'Новый канал продаж — тот же каталог, без второго учёта',
                'Второй склад или касса включаются настройкой, не переездом',
                'Платите только за подключённые модули — тариф меняется в кабинете',
              ].map((item) => (
                <li className="growth__list-item" key={item}>
                  <div className="growth__list-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </div>
                  {item}
                </li>
              ))}
            </ul>

            <div className="growth__cta growth-anim">
              <button
                type="button"
                className="btn-primary"
                onClick={() => openLandingAgent('У меня растёт бизнес. Что в Markbase подключить в первую очередь, а что можно позже?')}
              >
                Спросить AI, что подключить сначала
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                </svg>
              </button>
              <a href={APP_REGISTER_URL()} className="btn-secondary">Начать бесплатно</a>
            </div>
          </div>
        </div>

        <div className="growth__image-wrapper growth-anim">
          <div className="growth__image-fade"></div>
          <LandingOptionalImage src={lpFile('b06_growth_stages.jpg')} alt="Рост бизнеса" className="growth__image" />
        </div>
      </section>

      <section id="integrations" className="landing-info-section landing-info-section--muted lp-reveal">
        <div className="landing-info-section__inner">
          <div className="landing-info-section__label">Интеграции</div>
          <h2 className="landing-info-section__title">Деньги, бумаги и доставка — без второго рабочего места</h2>
          <p className="landing-info-section__text">
            Всё, что обычно живёт в отдельных кабинетах и мессенджерах, подключается к тому же заказу. Включаете нужное — остальное не мешается.
          </p>
          <div className="landing-info-section__grid lp-icon-grid">
            {INTEGRATION_CARDS.map(({ Icon, title, text }) => (
              <article className="landing-info-card lp-icon-card" key={title}>
                <span className="lp-icon-card__icon" aria-hidden>
                  <Icon size={20} strokeWidth={2.1} />
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════ БЛОК #7 — AI WORKSHOP ═══════════════ */}
      <section id="assistants" className="ai-workshop">
        <div className="ai-workshop__content">
          <div className="ai-workshop__badge ai-anim">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
            ВЭЙГПТ Экосистема
          </div>

          <h2 className="ai-workshop__title ai-anim">
            Помощники под конкретные задачи
          </h2>

          <p className="ai-workshop__subtitle ai-anim">
            Нажмите на карточку — помощник откроется с готовым вопросом, и вы увидите ответ на своих условиях, без регистрации.
          </p>

          <div className="ai-workshop__grid ai-anim">
            {AI_WORKSHOP_CARDS.map(({ Icon, title, text, prompt }) => (
              <button
                type="button"
                className="ai-workshop__card"
                key={title}
                onClick={() => openLandingAgent(prompt)}
              >
                <span className="ai-workshop__card-header">
                  <span className="ai-workshop__card-icon" aria-hidden>
                    <Icon size={19} strokeWidth={2.1} />
                  </span>
                  <span className="ai-workshop__card-title">{title}</span>
                </span>
                <span className="ai-workshop__card-text">{text}</span>
                <span className="ai-workshop__card-hint">
                  Спросить в чате
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                  </svg>
                </span>
              </button>
            ))}
          </div>

          <div className="ai-workshop__cta ai-anim">
            <button type="button" className="btn-primary" onClick={() => openLandingAgent()}>
              Задать свой вопрос{' '}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="ai-workshop__image-wrapper ai-anim">
          <div className="ai-workshop__fade-left"></div>
          <LandingOptionalImage src={`${lpFile('b07_ai_workshop.jpg')}?v=village`} alt="" className="ai-workshop__image" preferLight />
        </div>
      </section>

      {/* ═══════════════ КЕЙСЫ: витрина клиента и каналы ═══════════════ */}
      <section id="cases" className="lp-split lp-cases">
        <div className="lp-split__inner">
          <div className="lp-split__copy">
            <div className="lp-split__kicker">Как это выглядит</div>
            <h2 className="lp-split__title">Своя витрина, кабинеты и каналы на одном каталоге</h2>
            <p className="lp-split__lead">
              Товары, услуги и цифровые позиции живут в одном каталоге. Новый канал не создаёт второй кабинет.
            </p>
            <div className="lp-split__lib">
              {CASE_POINTS.map(({ id, Icon, title, text, prompt }) => (
                <button
                  type="button"
                  className="lp-lib-item"
                  key={id}
                  onClick={() => openLandingAgent(prompt)}
                >
                  <span className="lp-lib-item__icon" aria-hidden>
                    <Icon size={28} strokeWidth={2.1} />
                  </span>
                  <b>{title}</b>
                  <span>{text}</span>
                </button>
              ))}
            </div>
            <div className="lp-cases__cta">
              <button
                type="button"
                className="btn-primary"
                onClick={() => openLandingAgent('Хочу свою витрину, ЛК покупателя, кабинет работы и каналы Telegram, MAX, VK из одного каталога. С чего начать?')}
              >
                Как повторить у нас?{' '}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
                </svg>
              </button>
              <Link className="btn-secondary" to="/product/mplaza">
                Маркетплейс как канал
              </Link>
              <a
                className="btn-secondary"
                href={W7C_ECOSYSTEM_PRESENTATIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Презентации экосистемы
              </a>
            </div>
          </div>
          <figure className="lp-split__visual">
            <LandingOptionalImage
              src={`${lpFile('b19_case_vitrina.jpg')}?v=village`}
              fallbackSrc={lpFile('b01_hero_main.jpg')}
              alt=""
              className="lp-split__visual-photo"
              preferLight
            />
            <span className="lp-split__chip lp-split__chip--a">Свой домен</span>
            <span className="lp-split__chip lp-split__chip--b">Telegram · MAX · VK</span>
            <span className="lp-split__chip lp-split__chip--c">Один каталог</span>
          </figure>
        </div>
      </section>

      <section id="security" className="landing-info-section landing-info-section--dark lp-reveal">
        <div className="landing-info-section__inner">
          <div className="landing-info-section__label">Безопасность</div>
          <h2 className="landing-info-section__title">Кому видны ваши данные</h2>
          <p className="landing-info-section__text">
            Короткий ответ: только вам и тем сотрудникам, кому вы сами открыли доступ. Ниже — что это значит на практике.
          </p>
          <div className="landing-info-section__grid landing-info-section__grid--security lp-icon-grid">
            {SECURITY_CARDS.map(({ Icon, title, text }) => (
              <article className="landing-info-card lp-icon-card" key={title}>
                <span className="lp-icon-card__icon" aria-hidden>
                  <Icon size={20} strokeWidth={2.1} />
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="landing-info-section lp-pricing lp-reveal">
        <div className="landing-info-section__inner">
          <div className="landing-info-section__label">Тарифы</div>
          <h2 className="landing-info-section__title">Начните бесплатно — платите, когда вырастете</h2>
          <p className="landing-info-section__text">
            Не нужно выбирать тариф вслепую. Заведите компанию, посмотрите на своих товарах и заказах — и решите, что подключать дальше.
          </p>
          <div className="lp-pricing__card">
            <div className="lp-pricing__body">
              <h3 className="lp-pricing__heading">Что входит сразу</h3>
              <ul className="lp-pricing__list">
                {PRICING_INCLUDED.map((item) => (
                  <li key={item}>
                    <span className="lp-pricing__check" aria-hidden>
                      <Check size={14} strokeWidth={3} />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="lp-pricing__fine">Без карты и без обязательств. Модули включаются и выключаются по мере роста.</p>
            </div>
            <div className="lp-pricing__actions">
              <a className="btn-primary" href={APP_REGISTER_URL()}>Начать бесплатно</a>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => openLandingAgent(PRICING_AI_PROMPT)}
              >
                Спросить AI про тариф
              </button>
              <Link className="lp-pricing__link" to="/pricing">Смотреть тарифы и цены</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════ БЛОК #9 — ЧЕСТНЫЕ ГРАНИЦЫ ═══════════════ */}
      <section id="boundaries" className="landing-info-section lp-boundaries">
        <div className="landing-info-section__inner">
          <div className="landing-info-section__label">Границы</div>
          <h2 className="landing-info-section__title">Честно: что берём на себя, а что нет</h2>
          <p className="landing-info-section__text">
            Мы снимаем рутину и держим данные в порядке. Но не обещаем чудес там, где решение остаётся за вами.
          </p>
          <div className="lp-boundaries__grid">
            <article className="lp-boundaries__col lp-boundaries__col--do">
              <h3>
                <span className="lp-boundaries__chip lp-boundaries__chip--do">Берём на себя</span>
                Работает в системе
              </h3>
              <ul className="lp-boundaries__list">
                <li>Кабинет компании, права сотрудников и каталог</li>
                <li>Заказы, склад и доставку в одной цепочке</li>
                <li>Импорт прайсов и выгрузку в каналы продаж</li>
                <li>Документы, приём оплаты и помощь AI</li>
              </ul>
            </article>
            <article className="lp-boundaries__col lp-boundaries__col--dont">
              <h3>
                <span className="lp-boundaries__chip lp-boundaries__chip--dont">Не обещаем</span>
                Остаётся за вами
              </h3>
              <ul className="lp-boundaries__list">
                <li>Хаос в прайсе AI покажет, но решение — ваше</li>
                <li>Порядок на складе начинается с ваших правил</li>
                <li>Бухгалтерию не заменяем — стыкуемся с ней</li>
                <li>За сбои на стороне чужих сервисов не отвечаем</li>
              </ul>
            </article>
          </div>
          <div className="lp-boundaries__note">
            Меньше рутины и больше контроля — без «магии одной кнопкой». Так честнее и вам, и нам.
          </div>
        </div>
      </section>

      <section id="faq" className="landing-info-section landing-info-section--muted">
        <div className="landing-info-section__inner">
          <div className="landing-info-section__label">FAQ</div>
          <h2 className="landing-info-section__title">Вопросы, которые задают перед стартом</h2>
          <p className="landing-info-section__text">
            Не нашли свой вопрос — напишите в чат вверху страницы, ответим сразу.
          </p>
          <div className="lp-faq">
            {FAQ_ITEMS.map((item) => {
              const isOpen = openFaqId === item.id;
              return (
                <div className={`lp-faq__item${isOpen ? ' is-open' : ''}`} key={item.id}>
                  <h3 className="lp-faq__heading">
                    <button
                      type="button"
                      id={`lp-faq-trigger-${item.id}`}
                      className="lp-faq__trigger"
                      aria-expanded={isOpen}
                      aria-controls={`lp-faq-panel-${item.id}`}
                      onClick={() => setOpenFaqId(isOpen ? null : item.id)}
                    >
                      <span className="lp-faq__question">{item.q}</span>
                      <span className="lp-faq__chevron" aria-hidden>
                        <ChevronDown size={18} strokeWidth={2.4} />
                      </span>
                    </button>
                  </h3>
                  <div
                    className="lp-faq__panel"
                    id={`lp-faq-panel-${item.id}`}
                    role="region"
                    aria-labelledby={`lp-faq-trigger-${item.id}`}
                  >
                    <div className="lp-faq__panel-inner">
                      <p>{item.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════ ЗАКРЫВАЮЩИЙ CTA — всегда есть куда написать ═══════════════ */}
      <section id="contact-cta" className="lp-final-cta lp-reveal" aria-labelledby="lp-final-cta-title">
        <div className="lp-final-cta__inner">
          <span className="lp-final-cta__icon" aria-hidden>
            <MessagesSquare size={26} strokeWidth={2} />
          </span>
          <h2 className="lp-final-cta__title" id="lp-final-cta-title">
            Остались вопросы — напишите в чат
          </h2>
          <p className="lp-final-cta__text">
            Расскажите про свой бизнес: сколько товаров, где продаёте, что болит. Помощник подскажет, с чего начать и во сколько это обойдётся. Без регистрации и звонков.
          </p>
          <div className="lp-final-cta__actions">
            <button type="button" className="btn-primary" onClick={() => openLandingAgent()}>
              Написать в чат{' '}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
              </svg>
            </button>
            <a className="btn-secondary" href={APP_REGISTER_URL()}>Начать бесплатно</a>
          </div>
          <p className="lp-final-cta__fine">
            Предпочитаете почту или телефон — <Link to="/contact">наши контакты</Link>.
          </p>
        </div>
      </section>

      <PublicFooter showChatCta onWriteUs={() => openLandingAgent()} />

    </div>
  );
}
